import React from "react";
import { useQuery } from "@tanstack/react-query";
import { hubMutations } from "@surgia/api/src/queryStore/hub";
import { useSubmitPurchaseRequest } from "../../../hooks/useSubmitPurchaseRequest";
import GenericModal from "@surgia/ui/components/genericModal";
import Button from "@surgia/ui/components/button";
import { getLocalStorageItem } from "../../../pages/Catalog";
import { permissions } from "../../../permissions";
import PurchaseRequestSkeleton from "./PurchaseRequestSkeleton";

interface PurchaseRequestDetailsModalProps {
  open: boolean;
  onClose: () => void;
  purchaseRequestId: string;
  branchId: string;
  componentId: string;
  tenantId: string;
  hideFooter?: boolean;
  isSuperAdmin?: boolean;
}

const PurchaseRequestDetailsModal: React.FC<
  PurchaseRequestDetailsModalProps
> = ({
  open,
  onClose,
  purchaseRequestId,
  branchId,
  componentId,
  tenantId,
  hideFooter,
  isSuperAdmin,
}) => {
  const userPermissions = getLocalStorageItem("permissions", []);
  const { data: prDetails, isLoading } = useQuery({
    ...hubMutations.purchaseRequest.queries.getById({
      id: purchaseRequestId,
      componentId,
      tenantId,
      branchId,
    }),
    enabled: open && !!purchaseRequestId,
  });

  const submitPurchaseRequestMutation = useSubmitPurchaseRequest({
    branchId: branchId?.toString() || "",
    componentId: componentId || "",
    tenantId: tenantId?.toString() || "",
  });

  const handleSubmitPR = () => {
    if (prDetails?.id) {
      submitPurchaseRequestMutation.mutate({
        id: prDetails.id.toString(),
        componentId,
        tenantId,
        branchId,
      });
      onClose();
    }
  };

  if (isLoading) {
    return (
      <GenericModal
        title="Purchase Request Details"
        open={open}
        onClose={onClose}
        additionalStyles="!w-[600px] !min-h-[600px] !max-h-[80vh]"
        additionalStylesMain="flex flex-col min-h-[500px]"
      >
        <PurchaseRequestSkeleton />
      </GenericModal>
    );
  }

  if (!prDetails) {
    return (
      <GenericModal
        title="Purchase Request Details"
        open={open}
        onClose={onClose}
      >
        <div className="text-center py-8 text-gray-500">
          Purchase request not found
        </div>
      </GenericModal>
    );
  }

  return (
    <GenericModal
      title="Purchase request summary"
      additionalStyles="!w-[600px] !min-h-[600px] !max-h-[80vh]"
      additionalStylesMain="flex flex-col min-h-[500px]"
      open={open}
      onClose={onClose}
      footer={
        prDetails.status === "DRAFT" && (
          <Button
            variant="primary"
            className="w-full"
            onClick={handleSubmitPR}
            loading={submitPurchaseRequestMutation.isPending}
            disabled={
              submitPurchaseRequestMutation.isPending ||
              !prDetails.pr_items ||
              prDetails.pr_items.length === 0 ||
              prDetails.total_quantity === 0
            }
          >
            Submit Purchase Request
          </Button>
        )
      }
    >
      <div className="flex flex-col min-h-[500px] space-y-4">
        {/* Product List - Takes remaining space */}
        <div className="flex-1 min-h-0">
          <div className="space-y-3 h-full overflow-y-auto">
            {prDetails.pr_items?.map((item, index) => {
              // Get product features from the catalog_info feature_values
              const productFeatures =
                item?.tenant_product?.catalog_info?.feature_values || [];

              return (
                <div
                  key={index}
                  className="flex items-end justify-between py-2 border-b border-gray-100 last:border-b-0"
                >
                  <div className="flex-1">
                    <p className="text-sm text-gray-900">
                      {item.tenant_product?.tenant_product_name ||
                        item.tenant_product?.catalog_info?.name ||
                        "Product Name"}
                      {productFeatures.length > 0 && (
                        <span className="text-gray-600">
                          {" - "}
                          {productFeatures.map((feature, featureIndex) => (
                            <span key={featureIndex}>
                              {feature.featureVariant?.variant_name ||
                                feature.feature_Name}
                              {featureIndex < productFeatures.length - 1
                                ? " "
                                : ""}
                            </span>
                          ))}
                          {" × "}
                          {item.quantity}
                        </span>
                      )}
                    </p>
                  </div>
                  {(userPermissions?.includes(permissions.VIEW_PRODUCT_PRICE) ||
                    isSuperAdmin) && (
                    <div className="text-right">
                      <span className="text-sm font-medium text-gray-900">
                        {(item.tenant_product?.tenant_product_prices?.[0]
                          ?.price || 0) * (item?.quantity || 0)}{" "}
                        {item.tenant_product?.tenant_product_prices?.[0]?.currency?.code || ""}
                      </span>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </div>

        {/* Total Summary - Fixed at bottom */}
        {
          <div className="flex-shrink-0">
            {prDetails.purchase_request_price &&
              (userPermissions?.includes(permissions.VIEW_PRODUCT_PRICE) ||
                isSuperAdmin) && (
                <div className="border-t border-gray-200 pt-4 gap-2 flex flex-col">
                  <div className="flex justify-between items-end">
                    <span className="text-md text-gray-700">Total</span>
                    <span className="text-md text-gray-600">
                      {prDetails.purchase_request_price.price}{" "}
                      {prDetails.purchase_request_price.currency?.code}
                    </span>
                  </div>
                  <div className="flex justify-between items-center">
                    <span className="text-md text-gray-700">
                      Total quantity
                    </span>
                    <span className="text-md  text-gray-600">
                      {prDetails.total_quantity}{" "}
                    </span>
                  </div>
                </div>
              )}
            {hideFooter && (
              <div className="flex items-center justify-end gap-3 w-full mt-6 -mb-4">
                <Button
                  variant="outline"
                  onClick={onClose}
                  className="px-6 py-2 flex-1"
                >
                  Close
                </Button>
                <Button
                  variant="primary"
                  onClick={() => {}}
                  className="px-6 py-2 !bg-[#7144FF] hover:bg-purple-700 text-white flex-1"
                >
                  Add PR
                </Button>
              </div>
            )}
          </div>
        }
      </div>
    </GenericModal>
  );
};

export default PurchaseRequestDetailsModal;
